import { z } from 'zod';
import { cardDetailSchema, cardMemberSchema } from './card-detail';
import { customFieldTypeSchema } from './custom-field';
import { ulidSchema } from './id';
import { cardLabelSchema } from './label';
import { listSchema } from './list';

/**
 * Cross-board move and reconciliation — the contract layer for block **B21**.
 *
 * `BE-21.1` (`FR-TMPL-018`, what moves and what is dropped when a card crosses
 * boards, also spec'd as `FR-CARD-029` and `FR-BOARD-026`), `BE-21.2`
 * (`FR-TMPL-019`, label reconciliation — `OQ-4`), `BE-21.3` (`FR-TMPL-020`,
 * card-member reconciliation) and `BE-21.4` (`FR-TMPL-022`, move a whole list
 * with its cards, also spec'd as `FR-BOARD-012`). Two routes —
 * `POST /cards/:id/move-to-board` and `POST /lists/:id/move-to-board` — each
 * answering with the thing that moved **and a report of what the move cost**.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS FILE DOES NOT MOVE `BE-02.1`'s SEVEN, AND THE COUNT IS THE TRAP
 * `./index`'s HEADER HAS ALREADY SPRUNG FOUR TIMES
 * ---------------------------------------------------------------------------
 * Like `./board-filter` (a parameter's contract), `./search` (a route's two
 * halves) and `./mention` (a primitive), this is **a route's request and
 * response contract and nothing more**. "A cross-board move" is not one of
 * `BE-02.1`'s eight nouns; the nouns it touches — card, list, label, member —
 * all have files already, and every view below either *is* one of those shapes
 * or nests one. Seven of the eight have a file; **membership** is still the one
 * that does not, and `ls packages/shared/src` is still the instruction.
 *
 * ---------------------------------------------------------------------------
 * IT DECLARES NO CONSTANT OF ITS OWN, AND THAT IS `./card`'s RULE WITH ITS
 * SECOND BRANCH NEVER TAKEN
 * ---------------------------------------------------------------------------
 * `./card`'s standing rule is *import the constant that describes **your**
 * column if one exists, declare it if none does, and never share one across two
 * columns*. B21 introduces **no column** — it writes `cards.board_id`,
 * `cards.list_id`, `cards.position`, `cards.card_number`, `lists.board_id`,
 * `lists.position`, `labels`, `card_labels` and `card_members`, every one of
 * which was created by an earlier block — so there is nothing here for the
 * "declare it" branch to describe. Every bound below arrives with an imported
 * schema: `ulidSchema`, `cardLabelSchema`, `cardMemberSchema`,
 * `cardDetailSchema`, `listSchema`. A `MOVE_*_MAX_LENGTH` in this file would be
 * a bound over somebody else's column, which is the failure the rule names.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `OQ-4` IS ANSWERED AND THE ANSWER IS NOT IN THIS FILE — DELIBERATELY
 * ---------------------------------------------------------------------------
 * **The answer: colour is part of a label's identity.** A card's label is
 * matched against the destination board's set on the **pair** `(name, colour)`,
 * compared so that two absent names match each other; a label with the same
 * name and a different colour is **not** a match, and takes `FR-TMPL-019`'s own
 * second branch — created on the destination if the member can edit that
 * board's labels, otherwise dropped — and is reported as `created` (or
 * `dropped`), **never** as `applied`. It is recorded where a requirement is
 * recorded: `docs/requirements/07-templates-and-reuse.md`, under
 * `FR-TMPL-019`'s edge cases and in that document's Open questions.
 *
 * The *rule* lives in `matchesDestinationLabel` /
 * `planLabelReconciliation` in `apps/server/src/cards/reconcile.ts`, and this
 * file carries no predicate at all. That is `./search`'s split for the same
 * mechanical reason (**this package is bundled into `apps/web`**) plus one that
 * is specific to reconciliation: the match runs against *the destination
 * board's whole label set*, which is a row set the client never holds and must
 * never be sent — a client-side matcher would need the destination's vocabulary
 * in the browser before the move, which is a board read the caller may not even
 * be entitled to at the moment they open the dialog.
 *
 * ⚠ The half of `OQ-4` a reader of this file must carry anyway, because it
 * decides how the three arrays below are read: **a destination board may end up
 * holding two labels of one name in two colours.** That is accepted, not
 * overlooked — `FR-TMPL-021`'s edge case already prescribes a later merge
 * prompt as the remedy for accumulated near-duplicates, and adopting the
 * destination's colour instead would change what every member sees on the card
 * front while `reconciliation` reported the label as carried across.
 *
 * ---------------------------------------------------------------------------
 * WHAT NEITHER BODY ACCEPTS, AND WHY EACH ABSENCE IS ENFORCED RATHER THAN
 * MERELY OMITTED
 * ---------------------------------------------------------------------------
 * Both bodies are `z.strictObject`, so every field below is a **422** rather
 * than a silently ignored key. That matters more here than on most bodies,
 * because each of these is a plausible thing for a client to send and each
 * would otherwise get a correct-looking 200 to a different question:
 *
 *   - **No `boardId` on the card body.** The destination board is *derived*
 *     from the destination list (`lists.board_id NOT NULL`, so every list
 *     resolves to exactly one board). This is `createCardBodySchema`'s
 *     argument, verbatim: a body carrying both can **disagree with itself** —
 *     a list on board A and a `boardId` naming board B — and there is no right
 *     answer to that request, only a 4xx for a self-contradiction the schema
 *     can make inexpressible instead.
 *   - **No `position` and no rank on either body — SC-2.** The card body names
 *     the *gap* with two neighbours and the server computes the rank inside the
 *     transaction; the list body names nothing at all, because the list is
 *     appended. `./rank`'s header is the authority ("no caller should ever
 *     compute a rank by hand"), and `FR-BOARD-026`'s "the destination position
 *     selector must offer top, bottom and each numeric slot" is satisfied by
 *     the neighbour pair exactly as `cardMoveBodySchema` satisfies it for a
 *     same-board drag.
 *   - **No `cardNumber` and no `shortLink` anywhere.** `OQ-2` is answered (B08):
 *     a moved card is **renumbered** on the destination by
 *     `allocateCardNumber`, and its `short_link` is **never** rewritten — the
 *     permalink is carried by the short link and the number and slug after it
 *     are cosmetic and ignored on lookup (`FR-EXP-015`, `FR-CARD-005`). Both
 *     are server facts; a caller who sends either is asking for something the
 *     product does not offer.
 *   - **No `dryRun` flag.** `FR-TMPL-023`'s *before* warning is `FE-21.2`'s and
 *     its prerequisite is two **separate read-only preview routes**, specified
 *     in `apps/server/README.md` §23. AC2 ("given I cancel at the warning, then
 *     the card is still on its original board unchanged") is a guarantee a
 *     route that *cannot* write makes structurally and a flag makes only by
 *     handler discipline.
 *
 * ⚠ **There is no `z.preprocess` in this file**, and its absence is worth one
 * line because `./board-filter` is full of it. That file needs it because
 * Fastify's *querystring* parser hands back a bare string for one value and an
 * array for two; these are **JSON bodies**, parsed by `JSON.parse`, where an
 * array is an array and a string is a string. Copying the preprocess idiom here
 * would add a coercion path nothing produces.
 */

// ---------------------------------------------------------------------------
// Inputs — what the two routes ACCEPT.
//
// `z.strictObject` on both, per the header. Neither carries a `.default()`
// beyond the two neighbours, and neither is `.partial()`: a move names its
// destination or it is not a move.
// ---------------------------------------------------------------------------

/**
 * `POST /cards/:id/move-to-board` — `FR-TMPL-018`, `FR-BOARD-026`,
 * `FR-CARD-029`.
 *
 * The card is in the path; the **destination list** is in the body, and the
 * destination *board* is whatever board that list is on.
 *
 * ---------------------------------------------------------------------------
 * ⚠ IT IS DELIBERATELY SHAPE-COMPATIBLE WITH `cardMoveBodySchema` AND IS NOT IT
 * ---------------------------------------------------------------------------
 * `./card`'s `cardMoveBodySchema` (B12, `POST /cards/:id/move`) carries the
 * same three field **names** with the same nullable/default semantics, and that
 * is a requirement rather than a coincidence: **the same `resolveGap` helper in
 * `apps/server/src/routes/cards.ts` consumes both**, against a structural
 * `{ previousCardId: string | null; nextCardId: string | null }`. Rename a
 * field here, or spell a neighbour `.optional()` instead of
 * `.nullable().default(null)`, and the inferred type stops satisfying that
 * parameter — which is at least a compile error today, and would be a silently
 * different meaning (absent vs. explicitly null) if the helper ever loosened.
 *
 * ⚠ **The two schemas are nevertheless separate declarations, and this one does
 * not import `cardNeighbourSchema`.** That primitive is **module-private** in
 * `./card` (it is not exported), and exporting it to share two lines would
 * widen another block's surface for the sake of removing a duplicate that is
 * two identifiers long. `./search`'s `searchBoardResultSchema` makes the same
 * call against `boardAddressSchema` and states the general form: *two routes
 * with the same shape today is not two routes with the same contract.* B12's
 * body and B21's body are already diverging — B12 refuses a cross-board
 * `listId` with a 404 by construction, this one **requires** one — and a shared
 * declaration would make the next divergence a change to somebody else's route.
 *
 * ---------------------------------------------------------------------------
 * A DESTINATION BOARD WITH NO LISTS CANNOT RECEIVE A CARD, AND THAT IS NOT A
 * DEFECT
 * ---------------------------------------------------------------------------
 * `listId` is required, so there is nothing to send for such a board.
 * `FE-21.1`'s picker will happily offer it and then have no list to pick; the
 * client must create a list first. Recorded here, in `apps/server/README.md`
 * §23 and on `FE-21.1`'s item line so it is not filed as a bug against the
 * route.
 */
export const cardMoveToBoardBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The list the card lands in, ON THE DESTINATION BOARD. Required — the ' +
      'destination board is DERIVED from this list and is deliberately not a ' +
      'field of its own. A list on the card’s CURRENT board is a 409 naming ' +
      'POST /cards/:id/move (FR-BOARD-025), which is the route for that ' +
      'gesture.',
  ),
  previousCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately ABOVE the gap this card lands in, in the ' +
        'destination list. Send this alone to land the card at the bottom of ' +
        'that list. Same meaning, same spelling and the same null convention ' +
        'as cardMoveBodySchema’s field of this name — one resolveGap serves ' +
        'both.',
    ),
  nextCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately BELOW the gap this card lands in, in the ' +
        'destination list. Send this alone to land it at the top. Omitting ' +
        'BOTH neighbours is legal and means the bottom of the destination ' +
        'list — that is how a card is moved onto an empty list.',
    ),
});

export type CardMoveToBoardBody = z.infer<typeof cardMoveToBoardBodySchema>;

/**
 * `POST /lists/:id/move-to-board` — `FR-TMPL-022`, `FR-BOARD-012`.
 *
 * One field. The list is in the path, the destination board is in the body, and
 * **there is nothing else to say**.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE MISSING `position` IS THE RESOLUTION OF A GENUINE CONFLICT BETWEEN TWO
 * DOMAIN DOCUMENTS, MADE STRUCTURAL
 * ---------------------------------------------------------------------------
 * `FR-TMPL-022` and `FR-BOARD-012` specify the same capability and disagree
 * about where the list arrives. `FR-TMPL-022`: the list "arrives with all of
 * its cards in order and **is appended to the destination board**".
 * `FR-BOARD-012`: the dialog asks for "a target position among that board's
 * lists", and its AC1 moves a list "at position 2".
 *
 * The tie-break is `CLAUDE.md`'s authority **rule 2** — release phase
 * membership is owned by `00-overview-and-mvp-scope.md` — and it is
 * unambiguous: `FR-TMPL-022` is **Phase 1** and `FR-BOARD-012` is **Phase 2**.
 * So the Phase-1 deliverable is `FR-TMPL-022`'s behaviour (appended) and
 * `FR-BOARD-012`'s target-position selector is a Phase-2 refinement of the same
 * capability, built in its own phase. ⚠ Rule 1 (behaviour is owned by the
 * domain document) does **not** discriminate here — `07-` and `01-` are both
 * domain documents — and `00-`'s duplicate-owner table must **not** carry the
 * argument, because `00-` never overrides behaviour; it corroborates only.
 *
 * ⚠ **So `{ boardId, position: 2 }` is a 422 and not a silently ignored key**,
 * which is `./board-filter`'s `?mode=all` decision arriving a second time: B19
 * read `FR-SEARCH-008`'s stricter mode, declined it, and shipped **no `?mode`
 * parameter at all — not even one defaulting to the implemented behaviour**, so
 * a client asking for the unbuilt behaviour learns that it asked rather than
 * reading a legitimate-looking answer to a different question. `previousListId`
 * and `nextListId` are refused on the same footing, and so is any spelling of
 * "put it at the end", because the end is the only place it can go.
 *
 * ⚠ **The same ruling declines `FR-BOARD-012` AC2** ("only boards the user is a
 * member of are offered"), and the two are declined **together, from one
 * ruling**, because enforcing AC2 while declining AC1 would be cherry-picking
 * from a Phase-2 requirement. What the server enforces on the destination is
 * `requireBoardWriter` — the exact spelling of `FR-TMPL-022`'s own "any board
 * the member can edit" — and offering is `FE-21.1`'s job, recorded as its named
 * obligation. `apps/server/README.md` §23 carries the price of that decision in
 * full.
 */
export const listMoveToBoardBodySchema = z.strictObject({
  boardId: ulidSchema.describe(
    'The board this list moves to, WITH ALL OF ITS CARDS. The list is ' +
      'APPENDED to that board’s strip — there is no position field, and ' +
      'sending one is a 422 rather than a silently ignored key ' +
      '(FR-TMPL-022 is Phase 1 and appends; FR-BOARD-012’s target-position ' +
      'selector is Phase 2). The list’s own board is a 409 naming ' +
      'PATCH /lists/:id/position (FR-BOARD-011).',
  ),
});

export type ListMoveToBoardBody = z.infer<typeof listMoveToBoardBodySchema>;

/**
 * `GET /cards/:id/move-preview` — `FE-21.2`'s read-only prerequisite for
 * `FR-TMPL-023`.
 *
 * This is a query string, not a JSON body. It carries both ids because the
 * browser has selected a board and then a list inside it; the server still
 * resolves the destination board from the list, and rejects a contradictory
 * pair as a folded "No such list" rather than planning against two sources of
 * truth.
 */
export const cardMovePreviewQuerySchema = z.strictObject({
  boardId: ulidSchema,
  listId: ulidSchema,
});

export type CardMovePreviewQuery = z.infer<typeof cardMovePreviewQuerySchema>;

/**
 * `GET /lists/:id/move-preview` — the board-only preview matching
 * `POST /lists/:id/move-to-board`.
 */
export const listMovePreviewQuerySchema = z.strictObject({
  boardId: ulidSchema,
});

export type ListMovePreviewQuery = z.infer<typeof listMovePreviewQuerySchema>;

// ---------------------------------------------------------------------------
// Views — what the two routes RETURN.
//
// ⚠ `z.object`, which STRIPS an unknown key rather than refusing it — the exact
// inverse of the inputs above, and the trap `./notification`'s header records:
// `safeParse` on an extra field SUCCEEDS, so an assertion that a field is
// absent from one of these must go through `Object.keys(schema.shape)`. Every
// shape below either is an imported view or nests one; not one field of
// `cardDetailSchema`, `listSchema`, `cardLabelSchema` or `cardMemberSchema` is
// restated here.
// ---------------------------------------------------------------------------

/**
 * What the move cost — `FR-TMPL-019` ("the member **is told** which labels were
 * created and which were dropped"), `FR-TMPL-020` ("the removal **is
 * reported** to the actor") and `FR-TMPL-022` AC2 ("the actor is shown a
 * **single consolidated report** of what changed").
 *
 * ---------------------------------------------------------------------------
 * ONE SHAPE FOR ONE CARD AND FOR A WHOLE LIST, WITH NO PER-ENTRY COUNTS
 * ---------------------------------------------------------------------------
 * The list route's report is the **union across every card**, each label and
 * each user appearing exactly once — `labels.applied`/`labels.created`
 * deduplicated on the **destination** label id, `labels.dropped` on the
 * **source** label id, `members.removed` on the user id. That is
 * `FR-TMPL-022` AC2's "reported once, **not once per card**" made structural.
 *
 * A per-entry `cardCount` ("Blocked was created for 3 cards") was considered
 * and **declined**: it would make the two routes' reports two shapes for one
 * rule, and `FR-TMPL-023`'s warning — which is the only consumer either
 * requirement names — has no sentence to render it in. `cardsMoved` on
 * `listMoveToBoardResultSchema` gives the scale the deduplication removes.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE THREE LABEL ARRAYS ARE ONE SHAPE AND THEIR `id`s ARE NOT ONE KIND OF
 * THING
 * ---------------------------------------------------------------------------
 * See `dropped` below. A consumer that treats all three arrays' ids alike will
 * either 404 or — worse — edit the vocabulary of the board the card just left.
 * A separate schema for `dropped` was considered and declined: three arrays of
 * one shape is what lets `FR-TMPL-023`'s warning render with one component, and
 * a fourth schema for a description-of-a-non-event buys type safety the wire
 * cannot carry anyway. The field docstring is the cheaper enforcement, and it
 * names the two routes a consumer would wrongly pass the id to.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE REPORT COUNTS **SOURCE LABELS RESOLVED**; THE CARD COUNTS
 * **DESTINATION LABELS APPLIED**; AND THE TWO NUMBERS CAN LEGITIMATELY DIFFER
 * ---------------------------------------------------------------------------
 * Two source labels identical to each other (`labels` carries no unique index
 * and `POST /boards/:id/labels` refuses no duplicate) resolve to **one**
 * destination label: the first is `created`, the second is `applied` — because
 * a label that already exists on the destination by the time it is considered
 * *is* an existing destination label — both entries carry the same
 * `destinationId`, and the card ends up with **one** chip. So
 *
 *     applied.length + created.length
 *
 * is an **upper bound** on the card's resulting `labels` array, never a
 * prediction of it. A client that renders "2 labels carried across" beside a
 * card showing one chip is reading the number correctly and the sentence
 * wrongly. If you want the chips, read them off `card.labels`, which the card
 * route returns in the same response.
 *
 * ---------------------------------------------------------------------------
 * "NO CONSEQUENCES" IS AN EXPRESSION OVER THIS SHAPE, AND IT IS STATED HERE SO
 * `FE-21.2` DOES NOT INVENT A SECOND ONE
 * ---------------------------------------------------------------------------
 *     created.length === 0 && dropped.length === 0 && members.removed.length === 0
 *
 * ⚠ **`applied` is deliberately excluded from that test.** Applying an existing
 * destination label is not a consequence — nothing was created, nothing was
 * lost, and the card looks the same on both boards. `FR-TMPL-023` AC3 ("given a
 * move has no consequences, then no additional warning step appears") is that
 * expression on the client, and a version that also required `applied` to be
 * empty would put a warning dialog in front of the commonest successful move in
 * the product: a card whose labels the destination already has.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT IN HERE
 * ---------------------------------------------------------------------------
 *   - **No `custom_field_values`.** `FR-TMPL-021` is Parity-Later and **B56**'s;
 *     B21 touches that table not at all. The forward bill is that the move
 *     handler acquires a **third** reconciliation when B56 lands, and this
 *     schema a third key.
 *   - **No comments, checklists or attachments.** They travel untouched —
 *     `attachments`, `checklists`, `checklist_items` and `card_relations` carry
 *     no board- or workspace-scoped column, so `FR-TMPL-018`'s "must keep those
 *     attachments resolvable" is satisfied by construction and there is nothing
 *     to report. ⚠ One line for the reader who arrives from the copy dialog:
 *     **comments travel with a move (`FR-TMPL-018`) and are never copied
 *     (`FR-BOARD-027`'s edge case, `FR-CARD-030`). That is not a contradiction
 *     — move is not copy.**
 *   - **No activity-history key.** `FR-TMPL-018` AC3 requires the pre-move
 *     history not to be **shown** on the destination, which B21 delivers by
 *     writing nothing: every pre-move `activity_entries` row keeps
 *     `board_id = <source>` and no row is deleted or repointed. There is no
 *     outcome here to report.
 */
export const reconciliationSchema = z.object({
  /**
   * `FR-TMPL-019`. Three arrays, one element shape, **three different
   * meanings** — read each field before consuming any of them.
   */
  labels: z.object({
    /**
     * Source labels that found an exact `(name, colour)` match on the
     * destination board. The `id` is the **destination** label's.
     *
     * ⚠ Carried even though `FR-TMPL-019` only requires *created* and *dropped*
     * to be reported. It costs nothing and it makes AC1 — "the existing
     * destination label is applied and **no new label is created**" — assertable
     * from the report itself, rather than by diffing the card's resulting labels
     * against the source board's set, which is two extra reads for a fact the
     * server already knows.
     *
     * ⚠ Where the destination holds **several** exact matches (there is no
     * unique index on `labels(board_id, name, colour)`), the applied one is the
     * first in the board's own label order — `ORDER BY labels.position,
     * labels.id`, `FR-CARD-009` AC2's order. Unstated, two identical moves
     * apply two different destination labels and nothing anywhere disagrees.
     */
    applied: z.array(cardLabelSchema),
    /**
     * Source labels with no match, which were **created on the destination
     * board**. The `id` is the newly created **destination** label's, and it is
     * addressable: it is the id the `INSERT` used, minted by the same plan that
     * reported it.
     *
     * ⚠ Each of these also emits a `label.created` activity entry on the
     * destination board. That is **not** B15's seeded-labels case, where six
     * labels are created and nothing is emitted "because six `label.created`
     * rows at the head of every board's feed would attribute to the creator a
     * vocabulary they did not choose": here the actor **did** choose it, by
     * moving a card carrying that label, and `FR-TMPL-019` requires them to be
     * told it happened.
     */
    created: z.array(cardLabelSchema),
    /**
     * Source labels with no match that could **not** be created, and which the
     * card therefore arrives without — `FR-TMPL-019`'s second branch and
     * `FR-TMPL-023`'s "tell the user what they are about to lose".
     *
     * ⚠⚠ **THE `id` HERE NAMES A LABEL ON THE *SOURCE* BOARD — THE ONLY BOARD
     * WHERE THAT LABEL EXISTS — AND IT IS NOT ADDRESSABLE ON THE DESTINATION.**
     * A `dropped` entry is a **description of something that did not happen**,
     * not a handle. Passing its `id` to `DELETE /cards/:id/labels/:labelId` or
     * to `PATCH /labels/:id/name` addresses a label on the board the card just
     * left: the first is a no-op or a 404, and the second silently renames
     * another board's vocabulary. `applied[].id` and `created[].id` are
     * destination ids; this one is not, and the three arrays share a shape
     * precisely so that one component can render all three — which is also why
     * this warning has to live at the field.
     *
     * ⚠ **Empty is the ordinary answer today, and the branch is not dead.**
     * Creation is conditioned on "if the member can edit that board's labels"
     * (`FR-TMPL-019`), `POST /boards/:id/labels` guards on `requireBoardWriter`
     * and nothing else, and both move routes have already required exactly that
     * standing on the destination — so nothing B21 can be asked to do reaches
     * this branch. It becomes live the day `BE-38.1` narrows the writer guard
     * for observers or `BE-47.4` enforces `FR-BOARD-035`'s 950-label cap, at
     * which point the cap is simply a third reason to take a branch that is
     * already here and this shape does not change by a byte. `FR-TMPL-019` AC3
     * and AC4 are recorded as **not delivered by B21** in
     * `apps/server/README.md` §23 rather than claimed.
     */
    dropped: z.array(cardLabelSchema),
  }),
  /**
   * `FR-TMPL-020`. One array, and there is deliberately no `retained` beside it.
   *
   * ⚠ **The server's own planner returns both halves and only one crosses the
   * wire.** `planMemberReconciliation` in `apps/server/src/cards/reconcile.ts`
   * answers `{ removed, retained }`, because a preview needs to say what
   * survives; a *move response* does not, because the card comes back in the
   * same payload with its surviving members on it (`card.members`), and a
   * second listing of them would be the same fact twice with two chances to
   * disagree. `FR-TMPL-020` AC2 asks for "a message naming who was removed" and
   * nothing else.
   */
  members: z.object({
    /**
     * The people taken off the card because they hold **no `board_members` row
     * on the destination board** — `FR-TMPL-020` AC1, whose behaviour sentence
     * is "card members who are not **members of the destination board** are
     * removed".
     *
     * `cardMemberSchema`, so the entry carries a display name and initials:
     * AC2 needs a message that *names* who was removed, and a bare user id
     * cannot be rendered by a client that no longer has any reason to hold that
     * person's profile.
     *
     * ⚠ **The actor can be in this array, and that reads as a bug and is
     * correct.** A caller who may write the destination board without holding a
     * `board_members` row on it — a workspace admin, or a workspace member of a
     * `workspace_visible` destination — may legally perform the move, and is
     * then stripped off their own card by the same rule that strips anybody
     * else. It is reported here, which is exactly what `FR-TMPL-020` asks for,
     * and `apps/server/README.md` §23 carries the whole argument including the
     * losing reading and the fact that they cannot currently put themselves
     * back (`FR-PERM-017`/`BE-39.3`).
     *
     * ⚠ **No `member.removed` event is emitted for these rows**, and the
     * absence is load-bearing rather than tidy: that name **is** mapped in the
     * server's `NOTIFICATION_FROM_EVENT` to `removed_from_card`, so emitting it
     * per stripped member would send each of them "you were removed from a
     * card" — for a card on a board they cannot see — N notifications for one
     * gesture. `FR-TMPL-020` AC4 defers notification behaviour to
     * `09-notifications-and-personal-surfaces.md` in as many words. **The whole
     * outcome rides in this response instead**, addressed to the actor, which
     * is what both `FR-TMPL-019` and `FR-TMPL-020` actually ask for.
     */
    removed: z.array(cardMemberSchema),
  }),
  customFields: z.object({
    applied: z.array(
      z.object({
        source: z.object({
          id: ulidSchema,
          name: z.string(),
          type: customFieldTypeSchema,
        }),
        destination: z.object({
          id: ulidSchema,
          name: z.string(),
          type: customFieldTypeSchema,
        }),
      }),
    ),
    created: z.array(
      z.object({
        id: ulidSchema,
        name: z.string(),
        type: customFieldTypeSchema,
      }),
    ),
    optionsCreated: z.array(
      z.object({
        definitionId: ulidSchema,
        optionId: ulidSchema,
        label: z.string(),
      }),
    ),
    notAppliedToExisting: z.array(
      z.object({
        source: z.object({
          id: ulidSchema,
          name: z.string(),
          type: customFieldTypeSchema,
        }),
        existing: z.object({
          id: ulidSchema,
          name: z.string(),
          type: customFieldTypeSchema,
        }),
      }),
    ),
  }),
});

export type Reconciliation = z.infer<typeof reconciliationSchema>;

/**
 * What `POST /cards/:id/move-to-board` answers with — `FR-TMPL-018`,
 * `FR-BOARD-026` AC3, `FR-CARD-029`.
 *
 * ⚠ **`card` is the whole card back (`cardDetailSchema`), not `cardSchema`**,
 * and that is not generosity. The reconciliation is only interpretable beside
 * the card's *resulting* labels and members — `FR-TMPL-019` AC1's "no new label
 * is created" and `FR-TMPL-020` AC1's "the member is no longer on the card" are
 * both statements about the arrived card, and `cardSchema` can express
 * neither. `FR-BOARD-026` is invoked **from the card back**, so the card back
 * is what the caller needs on return; anything less means a second
 * `GET /cards/:id` on the same gesture.
 *
 * ⚠ **`cardDetailSchema` is nested, never extended and never widened.** It is
 * the response of `GET /cards/:id` and of B15's two card-label routes; a
 * `reconciliation` field added *to it* would put this block's report on three
 * shipped responses that have no reconciliation to report. `./index`'s rule in
 * one line: extend on a copy for what one route needs, widen the base only for
 * what every route serving that noun should carry — and this needs neither,
 * because a two-key envelope says "here is the card, and here is what the move
 * cost" without touching either half.
 */
export const cardMoveToBoardResultSchema = z.object({
  /**
   * The card as it now stands **on the destination board** — same `id`, same
   * `shortLink`, new `boardId`, `listId`, `position` and `cardNumber`.
   *
   * ⚠ `cardNumber` **has changed** and `shortLink` **has not**. `OQ-2`,
   * answered in B08 and implemented here: a card moved to another board is
   * renumbered (`allocateCardNumber`, above the destination's high-water mark
   * `boards.next_card_number`, `uq_cards_board_id_card_number` making a
   * collision inexpressible). So `ref` and `path` change too — the reference is
   * the destination's `KEY-NNNN` — while every earlier address keeps resolving:
   * the short link, and the source reference through `card_refs`
   * (docs/notes/card-keys.md). A client caching cards by `path` must re-read
   * this field; one caching by `shortLink` need not.
   */
  card: cardDetailSchema,
  reconciliation: reconciliationSchema,
});

export type CardMoveToBoardResult = z.infer<typeof cardMoveToBoardResultSchema>;

/**
 * What `POST /lists/:id/move-to-board` answers with — `FR-TMPL-022`,
 * `FR-BOARD-012`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE CARDS ARE NOT RETURNED, AND THE COUNT IS WHAT REPLACES THEM
 * ---------------------------------------------------------------------------
 * A list can hold any number of cards, so returning N card backs would make one
 * gesture answer with an **unbounded** response — which is the objection B16
 * raised against putting a card's comments on the card back, one level up. The
 * client refetches `GET /boards/:id/contents`, which is what B18's socket
 * reconciliation already does for every other board change, and `cardsMoved`
 * plus the consolidated `reconciliation` is what the actor needs to read
 * *before* that refetch lands.
 */
export const listMoveToBoardResultSchema = z.object({
  /**
   * The list as it now stands on the destination board — same `id`, same
   * `title`, new `boardId` and a new `position` at the far right of the
   * destination's strip.
   *
   * ⚠ Its `cardCount` and `cardsMoved` below are **two different measurements**
   * and are equal today by coincidence rather than by construction; see that
   * field.
   */
  list: listSchema,
  /**
   * How many cards travelled with the list — `FR-TMPL-022` AC1's "with all of
   * its cards".
   *
   * ⚠ **`z.number().int().nonnegative()`, not `z.int().min(0)`.** Zod 4 accepts
   * both; this package has one spelling for a non-negative integer
   * (`./board-contents`' `openCardCount`, `./list`'s `cardCount`, `./board`'s
   * counts, `./notification`'s unread count) and this file uses it. The `.int()`
   * half is not decoration: without it a fractional count parses.
   *
   * ⚠ **It counts the LIVE cards, and it is not `list.cardCount`.** The move
   * statement moves every row in the list — including **archived** cards, which
   * `FR-TMPL-022` AC3 requires to travel and stay archived, and including
   * soft-deleted ones, which travel because
   * `uq_cards_board_id_card_number` is unconditional and a row left behind
   * would sit on neither board. This number is for a **human** and excludes the
   * soft-deleted; `listSchema.cardCount` counts by `list_id` with
   * `deleted_at IS NULL` and does **not** exclude archived. So for a list of
   * one open and one archived card both read 2, and they would diverge the
   * moment a soft-deleted card were in the list. They measure different things
   * and neither is derivable from the other.
   */
  cardsMoved: z.number().int().nonnegative(),
  /**
   * The consolidated report — `FR-TMPL-022` AC2's "a **single** consolidated
   * report of what changed", deduplicated across every card that moved, so a
   * label created once for eight cards appears **once**. See
   * `reconciliationSchema`.
   */
  reconciliation: reconciliationSchema,
});

export type ListMoveToBoardResult = z.infer<typeof listMoveToBoardResultSchema>;
